/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 团队打标合规明细弹窗（近 7 天）
 *
 * 数据流：
 *   1. 弹窗 visible=true → useTeamLabelStats 调 stats/byMember
 *   2. groupID 从 redux 取，days 固定 7
 *   3. 后端返回 { total, members } → 渲染
 *   4. 接口失败 → 列表展示"加载失败，请稍后重试"
 */
import React, { useEffect, useState } from 'react'
import { Modal, message } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import type { RootState } from '../../../app/store'
import { fetchPhotoLabelStatsByMember } from '../../photoTag/api'
import {
  trackPhotoLabelStatsEnter,
  trackPhotoLabelStatsClick,
  StatsDataState
} from '../../photoTag/utils/track'
import './index.scss'

export interface MemberStats {
  userID: string
  name: string
  tagged: number
  untagged: number
  total: number
}

export interface TeamStatsData {
  total: { tagged: number; untagged: number; photos: number }
  members: MemberStats[]
}

export interface PhotoTagTeamStatsModalProps {
  visible: boolean
  onClose: () => void
}

function useTeamLabelStats(
  visible: boolean,
  groupID: string
): {
  loading: boolean
  data: TeamStatsData | null
  error: boolean
} {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TeamStatsData | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!visible || !groupID) {
      // 关闭时重置状态，避免下次打开看到旧团队的数据
      setData(null)
      setError(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(false)
    fetchPhotoLabelStatsByMember(groupID, 7)
      .then((res) => {
        if (cancelled) return
        setData({
          total: res.total,
          // 后端字段是 photos，UI 内部叫 total（拍照数），做一次映射保留兼容
          members: res.members.map((m) => ({
            userID: m.userID,
            name: m.name,
            tagged: m.tagged,
            untagged: m.untagged,
            total: m.photos
          }))
        })
      })
      .catch((err) => {
        if (cancelled) return
        setError(true)
        message.error(err?.message || '加载团队打标统计失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, groupID])

  return { loading, data, error }
}

const PhotoTagTeamStatsModal: React.FC<PhotoTagTeamStatsModalProps> = ({
  visible,
  onClose
}) => {
  const groupID = useSelector(
    (state: RootState) => state.group.groupId
  ) as string
  const { loading, data, error } = useTeamLabelStats(visible, groupID)

  const today = dayjs()
  const start = today.subtract(6, 'days')
  const range = `${start.format('M月D日')} — ${today.format('M月D日')}`

  const total = data?.total
  const members = data?.members || []

  // v4: 统计弹窗曝光埋点（区分 正常/空态/失败），数据加载完成后上报一次
  useEffect(() => {
    if (!visible || loading) return
    const state: StatsDataState = error
      ? 'error'
      : members.length === 0
        ? 'empty'
        : 'normal'
    trackPhotoLabelStatsEnter({
      number1: total?.tagged,
      number2: total?.untagged,
      number3: total?.photos,
      string1: state
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loading])

  // v4: 关闭埋点（返回箭头 + 蒙层/Esc 关闭都走这里）
  const handleClose = () => {
    trackPhotoLabelStatsClick({ clickItem: 'close' })
    onClose()
  }

  return (
    <Modal
      wrapClassName="photo-tag-team-stats-modal-wrap"
      open={visible}
      title={null}
      footer={null}
      closable={false}
      centered
      destroyOnHidden
      width={560}
      onCancel={handleClose}
    >
      {/* Header */}
      <div className="team-stats-header">
        <span className="team-stats-back" onClick={handleClose}>
          <ArrowLeftOutlined />
        </span>
        <h3 className="team-stats-title">团队打标合规明细</h3>
        <span className="team-stats-tag">👥 全体成员</span>
      </div>

      {/* 时间范围 */}
      <div className="team-stats-when">
        <span className="team-stats-when__label">近 7 天</span>
        <span className="team-stats-when__range">{range}</span>
      </div>

      {/* 汇总 */}
      <div className="team-stats-summary">
        <div className="team-stats-summary__col">
          <span className="team-stats-summary__num">
            {total?.tagged ?? '—'}
          </span>
          <span className="team-stats-summary__label">已打标</span>
        </div>
        <div className="team-stats-summary__col">
          <span className="team-stats-summary__num">
            {total?.untagged ?? '—'}
          </span>
          <span className="team-stats-summary__label">未打标</span>
        </div>
        <div className="team-stats-summary__col">
          <span className="team-stats-summary__num">
            {total?.photos ?? '—'}
          </span>
          <span className="team-stats-summary__label">拍照</span>
        </div>
      </div>

      {/* 成员列表 */}
      <div className="team-stats-list">
        {loading ? (
          <div className="team-stats-list__empty">加载中…</div>
        ) : error ? (
          <div className="team-stats-list__empty">
            <div className="team-stats-list__empty-icon">⚠️</div>
            <div className="team-stats-list__empty-title">加载失败</div>
            <div className="team-stats-list__empty-hint">
              请稍后重试，或联系管理员检查接口状态
            </div>
          </div>
        ) : members.length === 0 ? (
          <div className="team-stats-list__empty">
            <div className="team-stats-list__empty-icon">📊</div>
            <div className="team-stats-list__empty-title">暂无数据</div>
            <div className="team-stats-list__empty-hint">
              近 7 天没有成员拍照，或接口数据未到位
            </div>
          </div>
        ) : (
          members.map((m) => {
            const allUntagged = m.tagged === 0 && m.total > 0
            return (
              <div className="team-stats-row" key={m.userID}>
                <span className="team-stats-row__name">{m.name}</span>
                <div className="team-stats-row__stats">
                  <span
                    className={`team-stats-cell ${allUntagged ? 'is-bad' : ''}`}
                  >
                    <b>{m.tagged}</b>
                    <em>已打标</em>
                  </span>
                  <span className="team-stats-cell">
                    <b>{m.untagged}</b>
                    <em>未打标</em>
                  </span>
                  <span className="team-stats-cell">
                    <b>{m.total}</b>
                    <em>拍照</em>
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </Modal>
  )
}

export default PhotoTagTeamStatsModal
